import {IFetchedData, IFetchedListData} from 'powtoon-commons/reduxThunkUtils/fetched-data'
import {LicenceLevel, LicencePlan, LicenseCategory, SelfServeLicenseCategory} from 'powtoon-services/licensing/types'
import {Currency} from 'powtoon-services/payments/types'
import {HexColor, EmptyObject} from 'powtoon-versioning/src/currentSchema'

export enum LIMITED_FEATURES {
  AI = 'ai'
}

export interface LimitedFeatureState {
  available: number,
  total: number
}

export interface UserPermissions {
  isStaff: boolean,
  trialEndDate: Date | null,
  trialEndDateEc: Date | null,
  canUploadFonts: boolean,
  isClassroomUser: boolean,
  canBrandUserCharacters: boolean,
  canEditUserCharactersFace: boolean
  duration: number,
  recordingsLimit: number,
  recordingsCount: number,
  limitedExports: boolean,
  limitedExportsBalance: number,
  studioPowtoonsCount: number | null,
  studioPowtoonsLimit: number | null,
  teamFeaturesEnabled?: boolean,
  canStartTeamsTrial?: boolean,
  canUseSaao?: boolean,
  canUseTinting?: boolean,
  canManageAdminLocks?: boolean,
  canManageCompanyTemplates?: boolean,
  families: string[],
  soundPackages: string[],
  waterMark: string,
  quality: number,
  consumedExport: boolean,
  canUseTts?: boolean,
  canUseLipsync?: boolean,
  canUseBatchReplace?: boolean,
  canUseRealAvatars?: boolean | null,
  canUseCamera?: boolean,
  maxVideoTrackDuration: number,
  canUseAiScriptingTool?: boolean,
  canUseAiVideoProducer?: boolean,
  isTrialAvailable?: boolean,
  limitedFeatures: {
    [featureType: string]: LimitedFeatureState | undefined
  },
  canUseTextToImage?: boolean | null,
}

export interface UserBillingInfo {
  currency: Currency
}

export interface UserPaymentBillingInfo {
  year: number,
  month: number,
  billingType: string,
  billingUrl: string,
  lastFour?: string,
  companyName?: string | null,
  address1?: string | null,
  address2?: string | null,
  city?: string | null,
  country?: string | null,
  state?: string | null,
  postalCode: string | null,
  phone?: number | null,
  firstName?: string | null,
  lastName?: string | null,
  fullName?: string | null,
  vatNumber?: string | null
}

export type UserId = string

export enum DocumentRole {
  Editor = 'editor',
  Commenter = 'commenter'
}

export interface PublicUserProfile {
  id: UserId,
  firstName?: string,
  lastName?: string,
  thumbUrl?: string,
  documentRole?: DocumentRole
}

export interface UserProfile {
  id?: UserId,
  firstName?: string,
  lastName?: string,
  thumbUrl?: string,
  email?: string,
  isStaff?: boolean,
  licenseLevel: LicenceLevel,
  licensePlan: LicencePlan,
  licenseCategory?: LicenseCategory
  lastLoginDate: string, // when BE created current session
  previousLoginDate: string, // when BE created previous session (which is expired now)
  accountDate: string
}
export interface UserProfileInfo {
  value: UserProfile, status: number
}


export enum StartFromScratchOptions {
  Required = 'required',
  Optional = 'optional',
  NotAllowed = 'not_allowed'
}

export interface UserRecommendations {
  home: {
    startFromScratch: StartFromScratchOptions,
    collections: {
      recommended?: string[]
    }
  }
}

export type FetchedUserRecommendations = IFetchedData<UserRecommendations>

export interface InterimOnboardingAnswer {
  questionId: string,
  answerId: string
}

export interface UserDashboardSettings {
  interimOnboardingAnswers: InterimOnboardingAnswer[],
  onboardingReferralAnswers: InterimOnboardingAnswer[],
  selectedVideoTypeId: string,
  selectedVideoTypes: string[],
  jobTitleCategory: string,
  jobFunction: string,
  toolbarState: string,
  leftMenuState: string,
  lastVisit: string,
  currentVisit: string,
  acknowledgeData?: string[],
  notifications: {
    powtoonCapture: {
      numberOfTimesShown: {
        [powtoonId: string]: number
      }
    },
    powtoonTranslation: {
      shown?: boolean,
      shownMap: {
        [powtoonId: string]: true
      }
    }
  },
  teamsContactFormSubmissionDate?: number,
  pqlFormsSubmissionDates?: Record<string, number>
  manageBrandFormSubmissionDate?: number,
  bookDemoFormSubmissionDate?: number,
  onboardingIsStarted: boolean,
  onboardingStepsCompletion: {
    templateChooser?: boolean,
    templateSelected?: boolean,
  }
}

export interface UserCharacterBuilderSettings {
  isBrandingBannerSeen: boolean
}

export interface PreferencesSettings {
  autoConfirmSwapVideo: boolean,
  autoConfirmReplaceSlide: boolean
}
export type NewFeatureViewStatus = 'new' | 'popup-shown' | 'dismissed'

export interface NewFeatureState {
  dismissedTimestamp?: number,
  viewState: NewFeatureViewStatus
}
export interface NewFeatures {
  [key: string]: NewFeatureState
}

export interface TipsSettings {
  version: number | string
}

export interface UserFavorites {
  fonts: UserFavoriteFont[],
  colors: UserColor[]
}

export interface UserColor {
  id: string,
  color: HexColor
}

export type UserColorPalette = 'my' | 'org'

export interface UserFavoriteFont {
  family: string,
  creationDate: string
}

export interface RevisionModeNotifications {
  [powtoonId: string]: boolean
}

export interface UserStudioSettings {
  trialActivatedMessageSeen: boolean,
  isLiked: boolean,
  isQuickMenuShown: boolean,
  tips: TipsSettings,
  favorites: UserFavorites,
  preferences: PreferencesSettings,
  newFeatures: NewFeatures,
  notifications: {
    revisionMode: RevisionModeNotifications
  }
  version: '02.03',
  colorPicker: {
    lastColorPalette: 'my' | 'org'
  }
}

export interface UserProfileExtension {
  userProfile: IFetchedData<UserProfile>
}

export interface UserPermissionsExtension {
  permissions: IFetchedData<UserPermissions>
}

export interface UserBillingInfoExtension {
  billingInfo: IFetchedData<UserBillingInfo>
}

export interface UserRecommendationsExtension {
  recommendations: FetchedUserRecommendations
}

export interface UserSettings {
  dashboard: IFetchedData<UserDashboardSettings>,
  studio: IFetchedData<UserStudioSettings>,
  characterBuilder: IFetchedData<UserCharacterBuilderSettings>
}

export interface UserSettingsExtension {
  settings: UserSettings
}

export interface UserOrgPermissions {
  orgPermissions: string[],
  groupPermissions: {[key: string]: string[]}
}

export interface UserCurrentPlanData {
  plan_code: string,
  intervalLength: number,
  intervalUnit: string,
  category: SelfServeLicenseCategory | LicenseCategory,
  symbol: string,
  description: string,
  name: LicencePlan,
  price: string,
  nextBillingDate: string,
  isEduPlan: boolean
}

export type UserCurrentPlan = UserCurrentPlanData | EmptyObject

export interface UserCurrentPlanExtension {
  currentPlan: IFetchedData<UserCurrentPlan>
}

export interface UserOrgPermissionsExtension {
  userOrgPermissions: IFetchedData<UserOrgPermissions>
}

export interface UserOrgRole {
  id: string,
  title: string,
  parentGroupId: string,
  thumbUrl?: string
  createdBy: number,
  userCount: number,
  // NOTE: these are string representations of
  creationDate: string,
  joinDate: string
}
export interface UserOrgRolesExtension {
  userOrgRolesInGroups: IFetchedListData<UserOrgRole>
}

export interface CurrentUserState extends UserProfileExtension,
  UserPermissionsExtension,
  UserRecommendationsExtension,
  UserSettingsExtension,
  UserOrgRolesExtension,
  UserBillingInfoExtension,
  UserOrgPermissionsExtension,
  UserCurrentPlanExtension {}

export interface CurrentUserMapExtension {
  'powtoon@@currentUser': CurrentUserState
}

export interface IChangePasswordApiArgs {
  oldPassword: string,
  newPassword: string,
  repeatPassword: string
}

export interface NotificationDescriptor {
  notificationId: string,
  eventId: string,
  recipients: string[],
  notificationType: string,
  header: string,
  body: string,
  twit: string
}

